import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ImageUploadField from '../../components/ImageUploadField';
import { fetchData, postData } from '../../helpers/apiUtils';
import { CatalogBroker, FullProfile, HubBroker, HubSite } from './types';
import { NEW_PROFILE_HELP } from './help';

interface Props {
  open: boolean;
  sites: HubSite[];
  brokers: HubBroker[]; // from the matrix (has per-site presence)
  onClose: () => void;
  onCreated: (profile: FullProfile) => void; // open the editor on the new profile
}

/**
 * Create a broker profile for a portal. Pick an existing broker (or add a new one),
 * the portal, and where the starting content comes from: blank, the master version,
 * or a copy of the same broker on another portal.
 */
const NewProfileDialog: React.FC<Props> = ({ open, sites, brokers, onClose, onCreated }) => {
  const [catalog, setCatalog] = useState<CatalogBroker[]>([]);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [brokerSlug, setBrokerSlug] = useState('');
  const [newBroker, setNewBroker] = useState({ name: '', slug: '', logo_url: '' });
  const [siteKey, setSiteKey] = useState('');
  const [status, setStatus] = useState('draft');
  const [source, setSource] = useState<string>('blank');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    fetchData<CatalogBroker[]>('/api/broker-hub/brokers').then((r) =>
      setCatalog(r.success && r.data ? r.data : []),
    );
  }, [open]);

  // Portals where THIS broker already has a profile (candidates to copy from) + whether the target exists.
  const selectedMatrixBroker = useMemo(
    () => brokers.find((b) => b.slug === brokerSlug),
    [brokers, brokerSlug],
  );
  const copyableSites = useMemo(() => {
    if (!selectedMatrixBroker) return [];
    return sites.filter((s) => s.site_key !== siteKey && selectedMatrixBroker.profiles[s.site_key]);
  }, [selectedMatrixBroker, sites, siteKey]);
  const hasMaster = Boolean(selectedMatrixBroker?.master);
  const targetExists = Boolean(selectedMatrixBroker?.profiles[siteKey]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    let slug = brokerSlug;
    if (mode === 'new') {
      if (!newBroker.name || !newBroker.slug) {
        setError('Name und Slug des neuen Brokers sind nötig.');
        setBusy(false);
        return;
      }
      const br = await postData<CatalogBroker>('/api/broker-hub/brokers', {
        name: newBroker.name,
        slug: newBroker.slug.trim(),
        logo_url: newBroker.logo_url || null,
      });
      if (!br.success || !br.data) {
        setError(br.error || 'Broker konnte nicht angelegt werden.');
        setBusy(false);
        return;
      }
      slug = br.data.slug;
    }
    if (!slug || !siteKey) {
      setError('Broker und Portal auswählen.');
      setBusy(false);
      return;
    }
    // Resolve source: 'blank' | 'master' | profile id (from copyable site)
    let src: string | number = source;
    if (source !== 'blank' && source !== 'master') {
      const site = sites.find((s) => s.site_key === source);
      const cell = site && selectedMatrixBroker?.profiles[site.site_key];
      src = cell ? cell.id : 'blank';
    }
    const res = await postData<FullProfile>('/api/broker-hub/profiles', {
      broker_slug: slug,
      site_key: siteKey,
      status,
      source: src,
    });
    setBusy(false);
    if (res.success && res.data) {
      onCreated(res.data);
    } else {
      setError(res.error || 'Profil konnte nicht angelegt werden.');
    }
  };

  const brokerOptions = mode === 'existing' ? catalog : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Neues Broker-Profil</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {NEW_PROFILE_HELP}
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="Broker"
          value={mode === 'new' ? '__new__' : brokerSlug}
          onChange={(e) => {
            if (e.target.value === '__new__') setMode('new');
            else {
              setMode('existing');
              setBrokerSlug(e.target.value);
            }
          }}
          sx={{ mb: 2 }}
        >
          {brokerOptions.map((b) => (
            <MenuItem key={b.slug} value={b.slug}>
              {b.name}{' '}
              <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.6 }}>
                {b.slug}
              </Typography>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem value="__new__">＋ Neuen Broker anlegen…</MenuItem>
        </TextField>

        {mode === 'new' && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            mb={2}
            pl={1}
            borderLeft="2px solid #ccc"
          >
            <TextField
              size="small"
              label="Broker-Name"
              value={newBroker.name}
              onChange={(e) =>
                setNewBroker((n) => ({
                  ...n,
                  name: e.target.value,
                  slug:
                    n.slug ||
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-|-$/g, ''),
                }))
              }
            />
            <TextField
              size="small"
              label="Slug"
              value={newBroker.slug}
              onChange={(e) => setNewBroker((n) => ({ ...n, slug: e.target.value }))}
              helperText="Kleinbuchstaben, Bindestriche. Wird Teil der URL (brokerprofil-<slug>)."
            />
            {/* Hochladen statt tippen: getippte URLs zeigten bis 2026-09-02 auf FTs Mediathek —
                auf den anderen fuenf Portalen ist das ein Fremd-Hotlink, und eine relative
                (Capital.com) war dort schlicht ein 404. Das Textfeld bleibt als Rueckfall fuer
                SVG, das der Raster-Upload nicht annimmt. */}
            {/* ⚠ NUR das breite Logo. Ein Broker fuehrt seit 2026-09-07 ZWEI Rollen — breit
                (Steckbrief, Ueberschrift) und quadratisch (Kachel in der Rangliste). Die Marke
                fehlt hier bewusst: dieser Dialog legt eine SEITE an und den Broker nur nebenbei,
                ein zweites Bildfeld waere hier Ballast. Ohne Marke faellt die Rangliste auf das
                breite Logo zurueck — die Seite bleibt also heil, sie sieht in der Kachel nur
                gequetscht aus. Nachtragen im Katalog-Dialog (BrokerEditDialog). */}
            <ImageUploadField
              label="Logo breit (optional)"
              uploadUrl="/api/broker-content/upload-image?kind=logos"
              value={newBroker.logo_url}
              onChange={(url) => setNewBroker((n) => ({ ...n, logo_url: url }))}
            />
            <Typography variant="caption" color="text.secondary">
              Das quadratische Logo für die Rangliste trägst du danach im Broker-Katalog nach.
            </Typography>
          </Box>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="Portal"
          value={siteKey}
          onChange={(e) => setSiteKey(e.target.value)}
          sx={{ mb: 2 }}
        >
          {sites.map((s) => (
            <MenuItem key={s.site_key} value={s.site_key}>
              {s.name} ({s.site_key.toUpperCase()})
            </MenuItem>
          ))}
        </TextField>
        {targetExists && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Für diesen Broker existiert auf {siteKey.toUpperCase()} bereits ein Profil.
          </Alert>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="Start-Inhalt"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="blank">Leer beginnen</MenuItem>
          {hasMaster && <MenuItem value="master">Master-Version kopieren</MenuItem>}
          {copyableSites.map((s) => (
            <MenuItem key={s.site_key} value={s.site_key}>
              Kopie von {s.name} ({s.site_key.toUpperCase()})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <MenuItem value="draft">Entwurf (empfohlen)</MenuItem>
          <MenuItem value="live">Live</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={submit} disabled={busy || targetExists}>
          Anlegen & bearbeiten
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewProfileDialog;
